/**
 * WHATSAPP PAGE — il client WhatsApp del CRM (FILO-37,
 * docs/filo-37/2026-10-20-pagina-whatsapp-design.md).
 *
 * A sinistra i contatti con almeno un messaggio, dal più recente, e la ricerca in
 * rubrica per scrivere a chiunque; a destra la conversazione del contatto scelto
 * (`/whatsapp/:personId`): le sue sessioni in fila, le stesse della scheda, e il
 * compositore. Il compositore è sempre attivo: a finestra chiusa il backend
 * risponde 409 e compare il popup del modello approvato.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { formatDate, formatDateTime, useAsyncAutocomplete } from '@crm/shared';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { PlusGlyph } from '../../components/icons';
import { useToast } from '../../components/useToast';
import { getEntityFqn, resolveEntityLabel } from '../../entityRegistry/entityRegistry';
import type { Person } from '../../entities/person/personTypes';
import { relationshipApi } from '../../entities/relationship/relationshipApi';
import WhatsAppFormModal from '../../entities/whatsAppMessage/WhatsAppFormModal';
import { useWhatsAppEnabled } from '../../entities/whatsAppMessage/useWhatsAppEnabled';
import { useWhatsAppSender } from '../../entities/whatsAppMessage/useWhatsAppSender';
import {
  WhatsAppPhoneSelect,
  WhatsAppSenderSelect,
} from '../../entities/whatsAppMessage/WhatsAppNumberFields';
import { defaultWhatsAppPhone } from '../../entities/whatsAppMessage/whatsAppMessageTypes';
import { useWhatsAppPersonPolling } from '../../entities/whatsAppSession/useWhatsAppSessionPolling';
import { WhatsAppInlineComposer } from '../../entities/whatsAppSession/WhatsAppInlineComposer';
import { WhatsAppMessageBubbles } from '../../entities/whatsAppSession/WhatsAppMessageBubbles';
import {
  fetchWhatsAppConversations,
  fetchWhatsAppRecipient,
  fetchWhatsAppSessions,
  searchWhatsAppRecipients,
} from '../../entities/whatsAppSession/whatsAppSessionThunks';
import type { WhatsAppConversation } from '../../entities/whatsAppSession/whatsAppSessionTypes';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

/** Senza numeri WhatsApp della company la pagina non esiste: si torna alla home. */
export function WhatsAppPage() {
  const enabled = useWhatsAppEnabled();
  if (enabled === undefined) return null;
  return enabled ? <WhatsAppClient /> : <Navigate to="/" replace />;
}

function WhatsAppClient() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const personId = Number(useParams().personId) || 0;

  const [conversations, setConversations] = useState<WhatsAppConversation[] | null>(null);
  const [newChatOpen, setNewChatOpen] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      setConversations(await dispatch(fetchWhatsAppConversations()).unwrap());
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, [dispatch, toast]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  return (
    <div className="wa-root">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('whatsapp.page.title')}</h1>
          <span className="page-sub">{t('whatsapp.page.subtitle')}</span>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={() => setNewChatOpen(true)}>
            <PlusGlyph />
            {t('whatsapp.page.newChat')}
          </button>
        </div>
      </div>

      {newChatOpen && (
        <NewChat
          onClose={() => setNewChatOpen(false)}
          onSent={(id) => {
            void loadConversations();
            navigate(`/whatsapp/${id}`);
          }}
        />
      )}

      <div className="wa-page">
        <aside className="wa-page__list list-card">
          <div className="wa-page__search">
            <ContactSearch onPick={(p) => navigate(`/whatsapp/${p.personId}`)} />
          </div>
          {conversations?.length === 0 && (
            <p className="wa-page__empty">{t('whatsapp.page.empty')}</p>
          )}
          {conversations?.map((c) => (
            <button
              key={c.personId}
              type="button"
              className={`wa-page__item${c.personId === personId ? ' is-active' : ''}`}
              onClick={() => navigate(`/whatsapp/${c.personId}`)}
            >
              <span className="wa-page__name">{c.personName}</span>
              <span className="wa-page__date">{formatDateTime(c.lastMessageDate)}</span>
            </button>
          ))}
        </aside>

        <section className="wa-page__chat list-card">
          {personId > 0 ? (
            <Conversation
              key={personId}
              personId={personId}
              onNewMessage={() => void loadConversations()}
            />
          ) : (
            <p className="wa-page__empty">{t('whatsapp.page.choose')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

/** Ricerca in rubrica: qualunque contatto, anche senza conversazione. */
function ContactSearch({
  inputRef,
  value,
  label,
  onPick,
}: {
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Contatto scelto da mostrare nel campo (anche `null`, finché non c'è). Senza la
   * prop il campo è solo una ricerca e si svuota dopo la scelta.
   */
  value?: Person | null;
  label?: string;
  onPick: (person: Person) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Identity stabile: lo chiede useAsyncAutocomplete, che aggiunge pausa di battitura,
  // stato di caricamento e la regola «la scelta non è una ricerca».
  const searchPeople = useCallback(
    async (query: string): Promise<Person[]> => {
      const search = query.trim();
      if (search.length < 3) return [];
      try {
        return await dispatch(searchWhatsAppRecipients(search)).unwrap();
      } catch {
        return [];
      }
    },
    [dispatch],
  );

  const { loading, autocompleteProps, setQuery } = useAsyncAutocomplete<Person>({
    fetcher: searchPeople,
  });

  return (
    <Autocomplete
      {...autocompleteProps}
      value={value ?? null}
      loading={loading}
      loadingText={t('common.loading')}
      onChange={(_, p) => {
        if (!p) return;
        if (value === undefined) setQuery('');
        onPick(p);
      }}
      getOptionLabel={(p) => `${p.firstName} ${p.lastName}`.trim()}
      isOptionEqualToValue={(a, b) => a.personId === b.personId}
      noOptionsText={t('common.noResults')}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          placeholder={t('whatsapp.page.searchContact')}
          size="small"
        />
      )}
    />
  );
}

/**
 * Nuova chat: la finestra d'invio della scheda contatto (numero, mittente, messaggio,
 * allegato, modello a sessione chiusa) con sopra la scelta del destinatario.
 */
function NewChat({
  onClose,
  onSent,
}: {
  onClose: () => void;
  onSent: (personId: number) => void;
}) {
  const { t } = useTranslation();
  const [person, setPerson] = useState<Person | null>(null);
  const searchInput = useRef<HTMLInputElement>(null);

  return (
    <WhatsAppFormModal
      title={t('whatsapp.page.newChat')}
      personId={person?.personId ?? 0}
      phones={person?.phones ?? []}
      initialFocusRef={searchInput}
      recipientPicker={
        <div className="form-field">
          <ContactSearch
            inputRef={searchInput}
            value={person}
            label={t('whatsapp.page.recipient')}
            onPick={setPerson}
          />
        </div>
      }
      onClose={onClose}
      onCreated={() => person && onSent(person.personId)}
    />
  );
}

/**
 * La conversazione di un contatto: sessioni in fila, scelta dei numeri, compositore.
 * `onNewMessage` scatta a ogni messaggio nuovo, nostro o del contatto: la pagina ci
 * ricarica l'elenco, che non ha un polling suo (FILO-37, F1).
 */
function Conversation({
  personId,
  onNewMessage,
}: {
  personId: number;
  onNewMessage: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const sessions = useAppSelector((s) => s.whatsAppSessions.byPersonId[personId]);
  const [person, setPerson] = useState<Person | null>(null);
  const [failed, setFailed] = useState(false);
  // Il caricamento delle sessioni è finito, bene o male: da qui si sceglie il numero.
  const [sessionsSettled, setSessionsSettled] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const groupId = useCurrentGroupId();
  const chatRef = useRef<HTMLDivElement>(null);

  // Le sessioni arrivano dalla più recente; la chat si legge dalla più vecchia.
  const ordered = useMemo(() => [...(sessions ?? [])].reverse(), [sessions]);
  const latest = sessions?.[0];

  // Il mittente parte da quello dell'ultima sessione, se è ancora configurato.
  const { numbers, sender, setSender, preferredUnavailable } = useWhatsAppSender(
    sessionsSettled ? latest?.phoneNumberId : undefined,
  );

  useEffect(() => {
    async function load() {
      try {
        await dispatch(fetchWhatsAppSessions({ personId })).unwrap();
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      } finally {
        setSessionsSettled(true);
      }
    }
    async function loadPerson() {
      try {
        setPerson(await dispatch(fetchWhatsAppRecipient(personId)).unwrap());
      } catch (err) {
        setFailed(true);
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    }
    void load();
    void loadPerson();
  }, [dispatch, personId, toast]);

  // L'azienda accanto al nome: la principale, altrimenti la prima. Best-effort: se
  // non si risolve il nome resta da solo.
  useEffect(() => {
    const personFqn = getEntityFqn('Person');
    const businessFqn = getEntityFqn('Business');
    setBusinessName('');
    if (!personFqn || !businessFqn || !groupId) return;
    let cancelled = false;
    async function loadBusiness(personFqn: string, businessFqn: string) {
      try {
        const rels = (await relationshipApi.getByEntityBidirectional(groupId, personFqn, personId))
          .filter((r) => r.className2 === businessFqn);
        const rel = rels.find((r) => r.primary) ?? rels[0];
        if (!rel) return;
        const label = await resolveEntityLabel('Business', rel.classPK2);
        if (!cancelled) setBusinessName(label);
      } catch {
        // resta senza azienda
      }
    }
    void loadBusiness(personFqn, businessFqn);
    return () => { cancelled = true; };
  }, [groupId, personId]);

  // Il numero del contatto parte da quello dell'ultima sessione, se c'è; altrimenti
  // dal principale. Si aspetta ANCHE la fine del caricamento delle sessioni: con la
  // sola scheda arrivata prima, la risposta partirebbe dal numero principale invece
  // che da quello della conversazione. Se il caricamento fallisce si parte comunque
  // dal principale: meglio che non poter scrivere.
  useEffect(() => {
    if (!person || !sessionsSettled || phoneNumber) return;
    setPhoneNumber(
      defaultWhatsAppPhone(person.phones, latest?.phoneNumber) || (latest?.phoneNumber ?? ''),
    );
  }, [person, sessionsSettled, latest, phoneNumber]);

  // In fondo all'apertura e quando arriva un messaggio nuovo, non a ogni giro del
  // polling: quello sostituisce le sessioni anche se non è cambiato niente, e
  // riporterebbe giù chi sta leggendo i messaggi vecchi. Anche all'arrivo del
  // contatto: prima la chat non è montata, e le sessioni possono essere già qui.
  const lastMessageId = latest?.messages.at(-1)?.whatsAppMessageId;
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight });
  }, [lastMessageId, person]);

  // Sempre, anche a sessione scaduta: chi riscrive dopo 24h apre una sessione nuova,
  // e la chat la deve vedere senza ricaricare.
  useWhatsAppPersonPolling(personId);

  // L'ultimo messaggio visto dopo il primo caricamento (`null` = non ancora caricato):
  // un id diverso è un messaggio nuovo, anche il primo di una conversazione nuova.
  const seenMessageId = useRef<number | undefined | null>(null);
  useEffect(() => {
    if (!sessionsSettled) return;
    if (seenMessageId.current !== null && seenMessageId.current !== lastMessageId) {
      onNewMessage();
    }
    seenMessageId.current = lastMessageId;
  }, [sessionsSettled, lastMessageId, onNewMessage]);

  async function refresh() {
    try {
      await dispatch(fetchWhatsAppSessions({ personId })).unwrap();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  if (failed) return <p className="wa-page__empty">{t('whatsapp.page.choose')}</p>;
  if (!person) return null;

  const name = `${person.firstName} ${person.lastName}`.trim();

  return (
    <div className="wa-page__conversation">
      <div className="wa-page__head">
        <div className="wa-page__who">
          <span className="wa-page__name">{name}</span>
          {businessName && <span className="wa-page__business">{businessName}</span>}
        </div>
        <Link to={`/contacts/${personId}`} className="wa-page__open">{t('whatsapp.page.openCard')}</Link>
        {person.phones.length > 1 && (
          <WhatsAppPhoneSelect
            phones={person.phones}
            value={phoneNumber}
            onChange={setPhoneNumber}
            size="small"
          />
        )}
      </div>

      <div className="wa-page__messages" ref={chatRef}>
        {sessions && ordered.length === 0 && (
          <p className="wa-page__empty">{t('whatsapp.page.noMessages')}</p>
        )}
        {ordered.map((session) => (
          <div key={session.whatsAppSessionId} className="wa-page__session">
            <span className="wa-page__day">{formatDate(session.startDate)}</span>
            <WhatsAppMessageBubbles messages={session.messages} />
          </div>
        ))}
      </div>

      {phoneNumber ? (
        <WhatsAppInlineComposer
          personId={personId}
          phoneNumber={phoneNumber}
          phoneNumberId={sender}
          onSent={refresh}
          leading={
            <WhatsAppSenderSelect
              numbers={numbers}
              value={sender}
              onChange={setSender}
              preferredUnavailable={preferredUnavailable}
              labelOnly
              size="small"
              fullWidth={false}
              className="wa-page__sender"
            />
          }
        />
      ) : (
        // Finché le sessioni non arrivano il numero non è ancora scelto: non è «senza numeri».
        sessionsSettled && <p className="wa-page__empty">{t('whatsapp.noPhone')}</p>
      )}
    </div>
  );
}

export default WhatsAppPage;
